import { generateUUID } from '@kathondvla/sri-client/common-utils';
import SriClientError from '@kathondvla/sri-client/sri-client-error';
import { getNow, isAfter, isBeforeOrEqual } from '@kathondvla/sri-client/date-utils';
import { sriClient } from 'ReduxLoop/utils/apiConfig';
import {
  COORDINATOR,
  DIRECTEUR,
  LEIDINGGEVENDE_VAN_DE_VICARIALE_DIENST,
  AFDELINGSHOOFD,
  REGIODIRECTEUR,
  PROJECTLEIDER,
  VERANTWOORDELIJKE,
  VOORZITTER,
} from 'ReduxLoop/constants';
import { settings } from 'Js/config/settings';
import { getMainResponsibilityInfo } from './createTeamWizardModel';
import { root } from '@uirouter/angularjs';

class ValidationError {
  constructor(obj) {
    this.code = obj.code;
    this.duplicate = obj.duplicate;
    this.suggestedCode = obj.suggestedCode;
    this.duplicateVoorziterResponsibilities = obj.duplicateVoorziterResponsibilities;
  }
}

export const createOu = (state, validate) => {
  const ouKey = generateUUID();
  const ou = {
    $$meta: { permalink: `/kathondvla/organisationalunits/${ouKey}` },
    key: ouKey,
    description: state.description,
    names: [
      { value: state.name, startDate: state.startDate, type: 'OFFICIAL' },
      { value: state.shortName, startDate: state.startDate, type: 'SHORT' },
    ],
    $$displayName: state.shortName,
    type: state.selectedType,
    startDate: state.startDate,
  };
  if (!ou.description) {
    delete ou.description;
  }
  return ou;
};

export const createWorkgroup = (state, ou, validate) => {
  let organisationalUnit = ou;
  if (!organisationalUnit && state.organisation) {
    organisationalUnit = state.organisation;
  } else if (!organisationalUnit) {
    if (!validate) {
      throw new Error(
        'HOW! We komen in een stuk waar je alleen maar mag komen als we in validatiemode zitten en dat is niet het geval!'
      );
    }
    // Workgroups Api and Organisational Units Api should be one because you want to create and validate them both at the same time
    // Because they are now two separate api's we now have to do validation with an existing organisation, which is a workaround.
    organisationalUnit = {
      $$meta: { permalink: '/kathondvla/organisationalunits/ed036446-2913-476e-8528-485e0bbdfd3f' },
      key: 'ed036446-2913-476e-8528-485e0bbdfd3f',
    };
  }
  const wgKey = organisationalUnit.key;
  return {
    $$meta: { permalink: `/workgroups/${wgKey}` },
    key: wgKey,
    code: state.code || state.codeRaw,
    description: state.description,
    organisationalUnit: { href: organisationalUnit.$$meta.permalink },
    hasOffice365GroupIncludingMemberOUs: true,
    hasDrupalCollaboration: false,
    hasSharepoint: false,
    hasOutlookDistributionList: false,
    hasOutlookDistributionListIndirectMembers: false,
    hasOffice365Group: false,
    hasOffice365GroupIndirectMembers: false,
    hasOffice365GroupIncludingMemberOUsIndirectMembers: false,
    hasOutlookDistributionListIncludingMemberOUs: false,
    isCreatedByLoket: true,
  };
};

export const createResponsibility = (state, rootState, ou) => {
  const respKey = generateUUID();
  return {
    $$meta: { permalink: `/responsibilities/${respKey}`, created: new Date().toISOString() },
    key: respKey,
    person: { href: rootState.userState.user.$$meta.permalink },
    organisationalUnit: { href: ou.$$meta.permalink },
    organisation: { href: `/organisations/${ou.key}` },
    position: { href: VOORZITTER },
    startDate: state.startDate,
  };
};

let currentAbortWorkgroupValidationController = null;
export const validateWorkgroup = async (state) => {
  if (!state.codeRaw) {
    return;
  }
  try {
    const workgroup = createWorkgroup(state, null, true);
    if (currentAbortWorkgroupValidationController) {
      currentAbortWorkgroupValidationController.abort();
    }
    currentAbortWorkgroupValidationController = new AbortController();
    await sriClient.put(`${workgroup.$$meta.permalink}?dryRun=true`, workgroup, {
      cancel: currentAbortWorkgroupValidationController.signal,
    });
  } catch (error) {
    if (error instanceof SriClientError) {
      const { errors } = error.body;
      const codeNotUniqueError = errors.find((err) => err.code === 'code.not.unique');
      if (codeNotUniqueError) {
        throw new ValidationError(codeNotUniqueError);
      }
    }
    if (error.name && error.name === 'AbortError') {
      console.log(
        'A new request was send before the response of another request was received. The first request is canceled'
      );
      return;
    }
    console.log('UNEXPECTED_VALIDATE_WORKGROUP_ERROR', error);
    throw error;
  }
};

let currentAbortOrganisationValidationController = null;
export const validateOrganisation = async (state) => {
  const ou = createOu(state, true);
  let workgroup = null;
  try {
    if (currentAbortOrganisationValidationController) {
      currentAbortOrganisationValidationController.abort();
    }
    currentAbortOrganisationValidationController = new AbortController();
    await sriClient.put(`${ou.$$meta.permalink}?dryRun=true`, ou, {
      cancel: currentAbortOrganisationValidationController.signal,
    });
    return {};
  } catch (error) {
    if (error instanceof SriClientError) {
      const { errors } = error.body;
      let voorzitterResps = null;
      const notUniqueNameError = errors.find(
        (err) => err.code === 'name.not.unique'
      );
      if (notUniqueNameError) {
        const ouHref = notUniqueNameError.duplicates[0].organisationalUnit.href;
        const organisationalUnit = await sriClient.get(ouHref);
        const responsibilities = await sriClient.getAll('/responsibilities', {
          organisationalUnit: ouHref,
          expandPerson: 'SUMMARY',
        });
        voorzitterResps = responsibilities.filter(
          (resp) => resp.position && resp.position.href === VOORZITTER
        );
        if ((organisationalUnit.type === 'VERGADERGROEP' || organisationalUnit.type === 'LEERPLANCOMMISSIE') && responsibilities.length === 0) {
          [workgroup] = await sriClient.getAll('/workgroups', { organisationalUnit: ouHref });
          return {
            organisation: organisationalUnit,
            workgroup,
          };
        }
        if (!voorzitterResps) {
          voorzitterResps = await sriClient.getAll('/responsibilities', {
            organisationalUnit: ouHref,
            positions: VOORZITTER,
            expandPerson: 'SUMMARY',
          });
        }
        throw new ValidationError({
          code: notUniqueNameError.duplicates[0].nameType === 'OFFICIAL' ? 'name.not.unique' : 'shortname.not.unique',
          duplicate: organisationalUnit,
          duplicateVoorziterResponsibilities: voorzitterResps,
        });
      }
      const similarOrganisationWarning = errors.find(
        (err) => err.code === 'similar.organisationalunit'
      );
      if (similarOrganisationWarning) {
        const mostSimilar = similarOrganisationWarning.similarOrganisationalUnits.reduce((acc, cur) =>
          !acc ||
          ((acc.type === 'VERGADERGROEP' || acc.type === 'LEERPLANCOMMISSIE') && cur.type !== 'VERGADERGROEP' && cur.type !== 'LEERPLANCOMMISSIE') ||
          acc.dlDistance > cur.dlDistance ||
          (acc.dlDistance === cur.dlDistance && cur.nameType === 'SHORT' && acc.nameType === 'OFFICIAL')
            ? cur
            : acc
        );
        const ouHref = mostSimilar.organisationalUnit.href;
        const organisationalUnit = await sriClient.get(ouHref);
        voorzitterResps = await sriClient.getAll('/responsibilities', {
          organisationalUnit: ouHref,
          positions: VOORZITTER,
          expandPerson: 'SUMMARY',
        });
        throw new ValidationError({
          code: 'similar.organisation',
          duplicate: organisationalUnit,
          duplicateVoorziterResponsibilities: voorzitterResps,
        });
      }
    }
    if (error.name && error.name === 'AbortError') {
      console.log(
        'A new request was send before the response of another request was received. The first request is canceled'
      );
      return {};
    }
    console.log('UNEXPECTED_VALIDATE_ORGANISATION_ERROR', { ou, error });
    throw error;
  }
};

const getRecipients = async (mainRespInfo, state) => {
  if (mainRespInfo.isCongregatie || mainRespInfo.isVicarialeDienst || mainRespInfo.isRegion) {
    // regionOrCongregatieOrVd is on level 3 below KathOndVla -> dienstverlening -> regio/vicariale dienst/congregatie
    const regionOrCongregatieOrVdHref = mainRespInfo.regionOrCongregatieOrVdHref;
    const regionOrCongregatieOrVd = state.userState.ous[regionOrCongregatieOrVdHref];

    // 1. Managerial positions in the Region (regiodirecteur + coordinator), Vicariale Dienst (leidingevende vic d) or Congregatie (verantwoordelijke) of the user
    const managerHrefs = (
      await sriClient.getAll('/responsibilities', {
        organisationalUnit: regionOrCongregatieOrVdHref,
        positions: [REGIODIRECTEUR, COORDINATOR, LEIDINGGEVENDE_VAN_DE_VICARIALE_DIENST, VERANTWOORDELIJKE].join(','),
        date: getNow(),
      })
    ).map((resp) => resp.person.href);

    // 2. administrative coordinators of administrative team of the region / vicariale dienst / congregatie
    const subDepartmentRelations = await sriClient.getAll('/kathondvla/organisationalunits/relations', {
      type: 'IS_PART_OF',
      to: regionOrCongregatieOrVdHref,
      status: 'ACTIVE',
      expand: 'results.from',
    });
    // console.log('subRels', subDepartmentRelations);
    if (subDepartmentRelations.length > 0) {
      const administrativeSubDepartments = subDepartmentRelations
        .filter((rel) => rel.from.$$expanded.$$displayName.toLowerCase() === 'administratie')
        // .map((rel) => rel.from.href);
      if (administrativeSubDepartments.length === 0) {
        console.warn('No subteam administratie could be found in the region / vicariale dienst / congregatie', regionOrCongregatieOrVdHref);
        console.log(`recipients in region/vicariale dienst/congregatie ${regionOrCongregatieOrVd.$$displayName}:
          - loket responsible: ${settings.loketResponsible.href}
          - managers: ${managerHrefs.join(', ')}
          - team administratie: ${adminCoordinatorsOfDepartments.join(', ')}
        `);
        return [settings.loketResponsible.href, ...managerHrefs];
      }
      const adminCoordinatorsOfDepartments = (
        await sriClient.getAll('/responsibilities', {
          organisationalUnit: administrativeSubDepartments.map((rel) => rel.from.href).join(','),
          // positions: COORDINATOR,
          date: getNow(),
        })
      ).map((resp) => resp.person.href);
      console.log(`recipients in region/vicariale dienst/congregatie ${regionOrCongregatieOrVd.$$displayName}:
        - loket responsible: ${settings.loketResponsible.href}
        - managers: ${managerHrefs.join(', ')}
        - team administratie: ${adminCoordinatorsOfDepartments.join(', ')}
      `);
      return [settings.loketResponsible.href, ...managerHrefs, ...adminCoordinatorsOfDepartments];
    }
    console.log(`recipients in region/vicariale dienst/congregatie ${regionOrCongregatieOrVd.$$displayName}:
      - loket responsible: ${settings.loketResponsible.href}
      - managers: ${managerHrefs.join(', ')}
      - admin coordinators of administratie: ${adminCoordinatorsOfDepartments.join(', ')}
    `);
    return [settings.loketResponsible.href, ...managerHrefs];
  }
  if (mainRespInfo.afdeling) {
    const respPathWithoutTopLevel = mainRespInfo.mainPathToKathOndVla.slice(0, -1);
    // 1. Managerial positions in all levels of the person creating the team
    const managerResps = (
      await sriClient.getAll('/responsibilities', {
        organisationalUnit: respPathWithoutTopLevel.join(','),
        positions: [DIRECTEUR, COORDINATOR, PROJECTLEIDER, AFDELINGSHOOFD].join(','),
        date: getNow(),
      })
    );    
    let managers = null;
    let currentLevel = 0;
    // try to find the manager at the lowest possible level in the organisational hierarchy
    while (!managers && currentLevel < respPathWithoutTopLevel.length) {
      const managersAtLevel = managerResps.filter((resp) => resp.organisationalUnit.href === respPathWithoutTopLevel[currentLevel]);
      if (managersAtLevel.length > 0) {
        managers = managersAtLevel;
      }
      currentLevel++;
    }
    // remove afdelingshoofd if there are other managers who are not afdelingshoofd
    if (managers.some((resp) => resp.position.href !== AFDELINGSHOOFD)) {
      managers = managers.filter((resp) => resp.position.href !== AFDELINGSHOOFD);
    }
    const managerHrefs = managers.map((resp) => resp.person.href);

    // administrative people of the afdeling
    let personsWithAdministrativePositions = [];
    let personsInAdmintrativeTeam = [];
    if (mainRespInfo.afdeling.administrativePositions) {
      personsWithAdministrativePositions = (
        await sriClient.getAll('/responsibilities', {
          organisationalUnit: mainRespInfo.afdeling.href,
          positions: mainRespInfo.afdeling.administrativePositions.join(','),
          date: getNow(),
        })
      ).map((resp) => resp.person.href);
    }
    if (mainRespInfo.afdeling.administratie) {
      personsInAdmintrativeTeam = (
        await sriClient.getAll('/responsibilities', {
          organisationalUnit: mainRespInfo.afdeling.administratie,
          date: getNow(),
        })
      ).map((resp) => resp.person.href);
    }
    console.log(`recipients in afdeling ${mainRespInfo.afdeling.key.toLowerCase()}:
      - loket responsible: ${settings.loketResponsible.href}
      - managers: ${managerHrefs.join(', ')}
      - persons with administrative positions: ${personsWithAdministrativePositions.join(', ')}
      - persons in administrative team: ${personsInAdmintrativeTeam.join(', ')}
    `);
    return [settings.loketResponsible.href, ...managerHrefs, ...personsWithAdministrativePositions, ...personsInAdmintrativeTeam];
  }
  console.warn('@Magda: Could not find KathOndVla afdeling in main responsibility info of user, cannot send notification to department about new team creation');
  return [settings.loketResponsible.href];
}

const notifyDepartment = async (workgroup, rootState) => {
  const mainRespInfo = getMainResponsibilityInfo(rootState);
  console.log('mainRespInfo', mainRespInfo);
  if (!mainRespInfo) {
    console.warn('@Magda: Could not find main responsibility info in KathOndVla of user, cannot send notification to department about new team creation');
    return;
  }

  const recipients = await getRecipients(mainRespInfo, rootState);
  const uniqueRecipients = [...new Set(recipients)];

  await sriClient.post('/workgroups/notify-new-workgroup-created', {
    recipients: uniqueRecipients,
    workgroup: { href: workgroup.$$meta.permalink },
    loketResponsible: settings.loketResponsible,
  });
};

export const persistNewTeam = async (state, rootState) => {
  let ou = state.organisation;
  if (!ou) {
    ou = createOu(state);
    await sriClient.put(ou.$$meta.permalink, ou);
  } else {
    const currentName = ou.names.find((name) => name.type === 'OFFICIAL' && isBeforeOrEqual(name.startDate, getNow()) && isAfter(name.endDate, getNow()));
    const currentShortName = ou.names.find((name) => name.type === 'SHORT' && isBeforeOrEqual(name.startDate, getNow()) && isAfter(name.endDate, getNow()));
    if (currentName.value !== state.name || currentShortName.value !== state.shortName || ou.description !== state.description) {
      currentName.value = state.name;
      currentShortName.value = state.shortName;
      ou.description = state.description;
      await sriClient.updateResource(ou);
    }
  }

  let { workgroup } = state;
  if (!workgroup) {
    workgroup = createWorkgroup(state, ou);
  } else {
    // TODO check code!
    workgroup.hasOffice365GroupIncludingMemberOUs = true;
    workgroup.isCreatedByLoket = true;
  }

  const responsibility = createResponsibility(state, rootState, ou);
  await sriClient.post(`${responsibility.$$meta.permalink}/validate`, responsibility);
  await sriClient.updateResource(workgroup);
  await sriClient.updateResource(responsibility);

  const ret = {
    organisation: ou,
    workgroup,
    responsibility,
  };

  try {
    await notifyDepartment(workgroup, rootState);
  } catch (sendNotificationError) {
    console.error(sendNotificationError);
    ret.notifyDepartmentFailed = true;
  }

  return ret;
};
